import { Alert, Box, Stack, TextField, Typography } from '@mui/material';
import type { ChargeView, SupportedLocale } from '@stm/contracts';
import { translate } from '@stm/i18n';
import { formatCalendarDate } from './athletes';
import {
  allocationProblemKeys,
  formatMoney,
  type AllocationDraftLine,
  type AllocationDraftResult,
} from './billing';

function chargeLabel(
  charge: ChargeView | undefined,
  chargeId: string,
  locale: SupportedLocale,
): string {
  return charge === undefined
    ? chargeId.slice(0, 8)
    : `${charge.description} · ${formatCalendarDate(charge.dueOn, locale)}`;
}

export interface AllocationLinesProps {
  locale: SupportedLocale;
  currency: string;
  charges: readonly ChargeView[];
  draft: readonly AllocationDraftLine[];
  evaluation: AllocationDraftResult;
  /** The element id that names the group for assistive technology. */
  titleId: string;
  onChange: (draft: AllocationDraftLine[]) => void;
}

/**
 * The editable allocation lines of a payment: one amount per open charge, the total, and what stays
 * on the account as unallocated credit. The server repeats every check when the allocation is saved.
 */
export function AllocationLines({
  locale,
  currency,
  charges,
  draft,
  evaluation,
  titleId,
  onChange,
}: AllocationLinesProps): React.JSX.Element {
  return (
    <Box role="group" aria-labelledby={titleId}>
      <Typography id={titleId} sx={{ mb: 1 }}>
        {translate(
          locale,
          draft.length === 0
            ? 'billing.payment.allocation.none'
            : 'billing.payment.allocation.title',
        )}
      </Typography>
      <Stack spacing={1}>
        {draft.map((line, index) => {
          const charge = charges.find((candidate) => candidate.id === line.chargeId);
          return (
            <Stack
              key={line.chargeId}
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              sx={{ alignItems: { sm: 'center' } }}
            >
              <TextField
                size="small"
                label={chargeLabel(charge, line.chargeId, locale)}
                value={line.amountText}
                onChange={(event) => {
                  onChange(
                    draft.map((candidate, position) =>
                      position === index
                        ? { ...candidate, amountText: event.target.value }
                        : candidate,
                    ),
                  );
                }}
                slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                sx={{ minWidth: 280 }}
              />
              {charge !== undefined && (
                <Typography variant="body2" color="text.secondary">
                  {translate(locale, 'billing.col.outstanding')}:{' '}
                  {formatMoney(charge.outstandingMinor, currency, locale)}
                </Typography>
              )}
            </Stack>
          );
        })}
      </Stack>
      {evaluation.problem !== null && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {translate(locale, allocationProblemKeys[evaluation.problem])}
        </Alert>
      )}
      <Typography sx={{ mt: 1 }}>
        {translate(locale, 'billing.payment.allocation.total')}:{' '}
        {formatMoney(evaluation.allocatedMinor.toString(), currency, locale)}
        {' · '}
        {translate(locale, 'billing.payment.allocation.unallocated')}:{' '}
        {evaluation.problem === 'exceedsPayment'
          ? '–'
          : formatMoney(evaluation.unallocatedMinor.toString(), currency, locale)}
      </Typography>
    </Box>
  );
}
